import { CurrencyFormat, DateTimeFormat } from '@paypalcorp/worldready'
import moment from 'moment'
import { decryptClientId } from './app-utils'
import serverData from './server-data'

/*
    Function: formatApps((raw-json-response-from-developernodeweb))

    reformat data for PayPal UI 2.0 DropdownMenu
*/
export function formatAppOptions(apps, isSandbox) {
  if (!apps) {
    return []
  }

  return apps.map(({ clientid, name }) => ({
    value: !isSandbox ? decryptClientId(clientid) : clientid,
    primaryText: name,
  }))
}

export function formatAccountOptions(accounts) {
  if (!accounts) {
    return []
  }

  return accounts.map(({ primary_email_alias }) => ({
    value: primary_email_alias,
    primaryText: primary_email_alias,
  }))
}

export const formatDate = (worldReady, value) => {
  const date = value instanceof Date ? value : new Date(value)
  return new DateTimeFormat(worldReady, {
    style: 'datetime-short',
    timeZone: serverData.locality.timezone,
  }).format(date)
}

/*
    Function: formatEventTypeOptions(raw-json-response-from-developernodeweb)

    UI: PayPal UI 2.0 DropdownMenu

    developernodeweb response format (eventTypesDetails):

        "eventTypesDetails": [
            {
                "eventHeading": "Billing"
            },
            {
                "events": [
                    {
                        "shortDesc": "Plan activated",
                        "value": "BILLING.PLAN.ACTIVATED"
                    },
                    ...
                ]
            }
            ...
        ]

    Expected format:
        [
            {
                value: 'NO.EVENTTYPE',
                primaryText: 'All event types',
            },
            {
                unselectable: true,
                class: 'sectionGroup',
                primaryText: 'Payment',
            },
            {
                value: 'PAYMENT.AUTHORIZATION.CREATED',
                primaryText: 'Authorization created',
            },
            ...
        ]

*/
export function formatEventTypeOptions(eventTypes) {
  if (!eventTypes) {
    return []
  }

  const eventTypeOptions = [
    {
      value: 'NO.EVENTTYPE',
      primaryText: 'All event types',
    },
  ]

  eventTypes.forEach((obj) => {
    if (obj.eventHeading) {
      eventTypeOptions.push({
        unselectable: true,
        primaryText: obj.eventHeading,
      })
    }
    if (obj.events) {
      obj.events.forEach((objInner) => {
        eventTypeOptions.push({
          value: objInner.value,
          primaryText: objInner.shortDesc,
        })
      })
    }
  })

  return eventTypeOptions
}

/*
    Function: formatRows(raw-json-response-from-developernodeweb)

    UI: DashboardTable

    Expected 'DashboardTable' format based on Col format:
        {
            event_id: 'WH-1R202598W01871142-0KN76510EJ8310716',
            event_type: 'Payment sale completed',
            create_time: '20 May 2022 07:12:12',
            summary: 'Payment completed for $ 30.11 USD',
            resource: {
              id: '123456'
            },
            status: 'SUCCESS'
        }
*/
export function formatRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  const formattedRows = []

  rows.forEach((row) => {
    let eventType = row.event_type
    eventType = eventType.toLowerCase().replace(/\b\w/, (c) => c.toUpperCase())
    eventType = eventType.replace(/\./g, ' ')
    const createTime = formatDate(worldReady, row.create_time)

    formattedRows.push({
      event_id: row.id,
      event_type: eventType,
      create_time: createTime,
      summary: row.summary,
      resource_id: row.resource?.id,
      status: row.status,
    })
  })

  return formattedRows
}

/*
  Add a new property to the API Calls row called 'identifier' that is the first element
  of a concatenation of 'tag ids' and 'correlation id'

  Example:
    tag_id: ['PAYID-MMMKK3Y8CT60977U36073457', 'EC-85N32594PX165500M']
    correlation_id: '49a224720aafb'

    Concatentation: ['PAYID-MMMKK3Y8CT60977U36073457', 'EC-85N32594PX165500M', '49a224720aafb']
    identifier: 'PAYID-MMMKK3Y8CT60977U36073457'

*/
export function formatApiCallsRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  return rows.map((obj) => {
    const tagIds = obj.tag_id.toString().split(',')
    const correlationId = obj.correlation_id.toString().split()

    const eventType = `${obj.api_request.method} ${obj.url}`
    const createTime = formatDate(worldReady, obj.create_time)

    // concatentate 'tag ids' and 'correlation id', then take the first element as the identifier
    let identifier = tagIds.concat(correlationId)
    // remove any null/blank values
    identifier = identifier.filter((n) => n).shift()

    return { ...obj, identifier, event_type: eventType, create_time: createTime }
  })
}

/*
  API Call component of Error Log

  Transform API Call response and return
  expected structure for DataTable
*/
export function formatErrorLogRowsApiCalls(worldReady, rows) {
  if (!rows) {
    return []
  }

  return rows
    // skip successful (HTTP 2XX)
    .filter((obj) => !obj.http_status.toString().startsWith('2'))
    // transform to Error Log structure
    .map((obj, index) => {
      const description = obj?.api_response?.body?.message
      const eventType = `${obj?.api_request?.method} ${obj?.url}`
      const id = `row-${index}`
      const errorDate = formatDate(worldReady, obj?.create_time)

      return {
        id,
        ts: moment(obj?.create_time).format('x'),
        time: { value: errorDate },
        status: { value: obj?.http_status },
        debug: { value: obj?.correlation_id, clientId: obj?.client_id },
        event: { value: eventType },
        description: { value: description },
      }
    })
}

/*
  Error Log: Webhooks Events

  Transform Webhooks Events response from this API and return data for Error Log DataTable:

  GET /developer/api/dashboard/webhooks/filter-events
*/
export function formatErrorLogRowsWebhooks(worldReady, clientId, rows) {
  if (!rows) {
    return []
  }

  return rows
    // skip successful (aka non-DELIVERED)
    .filter((obj) => !obj.status.toString().startsWith('SUCCESS'))
    // transform to Error Log structure
    .map((obj, index) => {
      const description = obj.summary
      const eventType = obj.event_type
      const id = `wh-row-${index}`

      // convert from ISO 8601 to formatted:  2022-11-29T15:36:01Z => 29 Nov 2022 09:36:01
      const localDate = formatDate(worldReady, obj.create_time)

      return {
        id,
        ts: moment(obj.create_time).format('x'),
        time: { value: localDate },
        status: { value: obj.status },
        debug: {
          value: obj.id,
          eventId: obj.id,
          clientId,
        },
        event: { value: eventType },
        description: { value: description },
      }
    })
}

/*
  Error Log: Webhooks Transmissions

  Transform Webhooks Transmissions response from this API and return data for Error Log DataTable:

  GET https://localhost.paypal.com/developer/api/dashboard/webhooks/transmissions
*/
export function formatErrorLogRowsTransmissions(worldReady, rows) {
  if (!rows?.transmissions?.items) {
    return []
  }

  return rows.transmissions.items
    // skip successful (aka non-DELIVERED)
    .filter((obj) => !obj.status.toString().startsWith('DELIVERED'))
    // transform to Error Log structure
    .map((obj, index) => {
      const eventType = obj.classifiers.pairs['WHE-eventName']
      const clientId = obj.classifiers.pairs['WHE-clientId']
      const id = `wh-row-${index}`

      // convert from ISO 8601 to formatted:  2022-11-29T15:36:01Z => 29 Nov 2022 09:36:01
      const localDate = formatDate(worldReady, obj.creation_time)

      return {
        id,
        ts: moment(obj.creation_time).format('x'),
        time: { value: localDate },
        status: { value: obj.status },
        debug: {
          value: obj.classifiers.pairs['WHE-eventId'],
          eventId: obj.classifiers.pairs['WHE-eventId'],
          clientId,
        },
        event: { value: eventType },
        description: { value: obj.description },
      }
    })
}

export function formatNotificationsRows(worldReady, rows) {
  if (!rows) {
    return []
  }

  const formattedRows = []
  rows.forEach((row) => {
    formattedRows.push({
      email_to: row.email_to,
      email_subject: row.email_subject,
      date_received: formatRestApiAppsEpoch(
        worldReady,
        parseInt(row.date_recieved, 10),
      ),
      // date_received: format(
      //   new Date(parseInt(row.date_recieved, 10) * 1000),
      //   'dd MMM yyyy HH:mm:ss',
      // ),
      email_message: row.email_message,
    })
  })
  return formattedRows
}

const formatCurrency = (amount, currency, worldready) => {
  const currencyFormatter = new CurrencyFormat(worldready, { currency })
  return `${currencyFormatter.format(Number(amount))} ${currency}`
}

/*
  Recent Transaction
*/
export function formatRecentTransactionsRows(rows, worldready) {
  if (!rows) {
    return []
  }

  return rows
    // take the first five records
    .slice(0, 5)
    // transform to Recent Transactions structure
    .map((obj, index) => {
      const id = `row-${index}`
      const transactionDate = formatDate(worldready, obj.time_created)
      let paymentMethod
      const fundingSource = obj.extension?.money_movements?.[0]?.funding_source
      if (!fundingSource || fundingSource?.id === 'BALANCE') {
        paymentMethod = obj.counterparty?.email
      } else if (fundingSource?.last_four_digits) {
        paymentMethod = '•••• ' + fundingSource?.last_four_digits
      }
      return {
        id,
        time: { value: transactionDate },
        status: {
          isCompleted: obj.status === 'COMPLETED' ? true : false,
          isFailed: obj.status === 'FAILED' ? true : false,
          isPending: obj.status === 'PENDING' ? true : false,
          isHeld: obj.status === 'HELD' ? true : false,
          isUnClaimed: obj.status === 'UNCLAIMED' ? true : false,
          isRefunded: obj.status === 'REFUNDED' ? true : false,
          isPartiallyRefunded:
            obj.status === 'PARTIALLY_REFUNDED' ? true : false,
        },
        paymentMethod: { value: paymentMethod ? paymentMethod : '' },
        TransactionId: { value: obj.id },
        amount: {
          value: formatCurrency(
            obj.gross?.value,
            obj.gross?.currency_code,
            worldready,
          ),
        },
      }
    })
}

export function formatRestApiAppsEpoch(worldReady, epochTimestamp) {
  if (
    !epochTimestamp ||
    !Number.isInteger(epochTimestamp) ||
    epochTimestamp < 1000000000
  ) {
    return ''
  }
  const isMilliseconds = epochTimestamp.toString().length === 13
  const epochInSeconds = isMilliseconds ? epochTimestamp / 1000 : epochTimestamp
  const createdDate = moment.unix(epochInSeconds).format('DD MMM YYYY HH:mm:ss')
  return formatDate(worldReady, createdDate)
}

export function calculateExpiryTimeInHours(expiryDate) {
  // Convert expiryDate string to Date object
  const expiryTimeDate = new Date(expiryDate)

  // Calculate time until expiration in milliseconds
  const currentTime = new Date()
  const timeUntilExpiration = expiryTimeDate - currentTime

  // Convert milliseconds to hours and round down to the nearest whole number
  return Math.floor(
    timeUntilExpiration / (1000 * 60 * 60),
  )
}

export function calculateExpiryTimeInDaysHours(expiryTime) {
  const expiryTimeDate = new Date(expiryTime)

  const currentTime = new Date()
  const timeUntilExpiration = expiryTimeDate - currentTime

  const hoursUntilExpiration = Math.floor(
    timeUntilExpiration / (1000 * 60 * 60),
  )
  const days = Math.floor(hoursUntilExpiration / 24)
  const hours = hoursUntilExpiration % 24

  return `${days} day(s) and ${hours} hour(s)`
}

export function shortenedText(text, maxLength = 25) {
  // maxLength should be at least 8
  if (text.length <= maxLength) {
    return text
  }

  const endLength = 5
  const startLength = Math.max(0, maxLength - 3 - endLength) // 3 dots in between
  const start = text.slice(0, startLength)
  const end = text.slice(-endLength)

  return `${start}...${end}`
}
